import { Button } from '@/components/ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { DragDropProvider } from '@dnd-kit/react';
import { useEffect, useState } from 'react';
import { AccountSortGroup } from './account-sort-group';
import { AccountSortItem } from './account-sort-item';
import {
  Account,
  AccountState,
  getAccountGroups,
  useAccounts,
  useBudgetId,
  useSortAccountsApi,
} from '@liquidbudget/common';
import { move } from '@dnd-kit/helpers';

type Props = {
  onSuccess: () => void;
};

export function AccountSortForm({ onSuccess }: Props) {
  const budgetId = useBudgetId();
  const accounts = useAccounts();

  const activeAccounts = accounts.filter((account) => account.state === AccountState.ACTIVE);
  const { mutate: sortAccountsMutate, isPending } = useSortAccountsApi({ onSuccess });

  // State for grouped accounts and group order
  const [items, setItems] = useState<Record<string, Account[]>>({});
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [initialized, setInitialized] = useState(false);

  /**
   * Submits the new account order to the API with global positioning.
   * Calculates sequential positions starting from 0 across all groups.
   */
  const handleSubmit = () => {
    let position = 0;
    const activeOrderedIds: string[] = [];

    // Build ordered list with global positions based on group order
    groupOrder.forEach((groupName) => {
      const groupAccounts = items[groupName] || [];
      groupAccounts.forEach((account) => {
        activeOrderedIds.push(account.id);
        position++;
      });
    });

    // Add inactive accounts at the end
    const inactiveAccounts = accounts.filter((account) => account.state !== AccountState.ACTIVE);
    const inactiveOrderedIds = inactiveAccounts.map((account) => account.id);
    const ids = [...activeOrderedIds, ...inactiveOrderedIds];

    sortAccountsMutate({ budgetId, ids });
  };

  // Initialize state from current account groups - only once
  useEffect(() => {
    if (!initialized && activeAccounts.length > 0) {
      const { groups } = getAccountGroups(activeAccounts);
      const initialItems: Record<string, Account[]> = {};
      const initialGroupOrder: string[] = [];

      groups.forEach((group) => {
        initialItems[group.name] = [...group.accounts]; // Create new arrays
        initialGroupOrder.push(group.name);
      });

      setItems(initialItems);
      setGroupOrder(initialGroupOrder);
      setInitialized(true);
    }
  }, [activeAccounts, initialized]);

  // Don't render until initialized
  if (!initialized) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <ScrollArea className="max-h-[70vh] pr-3">
        <DragDropProvider
          onDragStart={() => {}}
          onDragOver={(event) => {
            const { source } = event.operation;
            if (source?.type === 'group') {
              return; // Don't update items when dragging groups
            }
            // Update account positions within/between groups
            setItems((prevItems) => move(prevItems, event));
          }}
          onDragEnd={(event) => {
            const { source } = event.operation;
            if (event.canceled) {
              return;
            }

            if (source?.type === 'group') {
              // Reorder groups
              setGroupOrder((prevGroups) => move(prevGroups, event));
            } else {
              // Finalize account moves
              setItems((prevItems) => move(prevItems, event));
            }
          }}
        >
          <div className="space-y-4">
            {groupOrder.map((groupName, groupIndex) => {
              const groupAccounts = items[groupName] || [];

              return (
                <AccountSortGroup
                  key={groupName}
                  id={groupName}
                  index={groupIndex}
                  name={groupName}
                  isCollapsed={isCollapsed}
                >
                  {groupAccounts.map((account, accountIndex) => (
                    <AccountSortItem
                      key={account.id}
                      id={account.id}
                      index={accountIndex}
                      group={groupName}
                      account={account}
                    />
                  ))}
                </AccountSortGroup>
              );
            })}
          </div>
        </DragDropProvider>
      </ScrollArea>

      <div className="flex justify-between mt-2">
        <div>
          <Button type="button" variant="secondary" onClick={() => setIsCollapsed(!isCollapsed)}>
            {isCollapsed ? 'Expand Groups' : 'Collapse Groups'}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button type="submit" isLoading={isPending} onClick={handleSubmit}>
            Sort Accounts
          </Button>
        </div>
      </div>
    </>
  );
}
